import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { torrefacoesService } from '../../../services/torrefacoes';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useDebounce } from '../../../hooks/useDebounce';

const MIN_SEARCH_LENGTH = 3;

interface Torrefacao {
  id: string;
  name: string;
  description: string;
  address: string;
  cityName: string;
  stateName: string;
  type: number;
  status: string;
  logoUrl: string;
  coverImageUrl: string;
  instagram: string;
  createdAt: string;
  updatedAt: string;
}

const TorrefacoesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [placeToDelete, setPlaceToDelete] = useState<Torrefacao | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const effectiveSearch = debouncedSearchTerm.length >= MIN_SEARCH_LENGTH ? debouncedSearchTerm : '';

  // Buscar torrefações
  const { data: placesData, isLoading: isLoadingPlaces } = useQuery({
    queryKey: ['torrefacoes', effectiveSearch, statusFilter, currentPage, pageSize],
    queryFn: async () => {
      return await torrefacoesService.getTorrefacoes({
        page: currentPage,
        pageSize,
        search: effectiveSearch,
        status: statusFilter !== 'all' ? statusFilter : undefined
      });
    }
  });

  const places = placesData?.places || [];
  const pagination = placesData?.pagination;

  // Deletar torrefação
  const deleteMutation = useMutation({
    mutationFn: (id: string) => torrefacoesService.deleteTorrefacao(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['torrefacoes'] });
      alert('Torrefação excluída com sucesso!');
      setDeleteModalOpen(false);
      setPlaceToDelete(null);
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Erro ao excluir torrefação');
    },
  });

  const handleDelete = (place: Torrefacao) => {
    setPlaceToDelete(place);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (placeToDelete) {
      deleteMutation.mutate(placeToDelete.id);
    }
  };

  // Função para mudar de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Função para aplicar filtros
  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset para primeira página ao aplicar filtros
  };

  if (isLoadingPlaces) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gerenciar Torrefações</h1>
        <p className="text-gray-600">Gerencie todas as torrefações do sistema</p>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Buscar por nome ou cidade..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">Todos os Status</option>
                <option value="Active">Ativas</option>
                <option value="Inactive">Inativas</option>
                <option value="Pending">Pendentes</option>
                <option value="Suspended">Suspensas</option>
              </select>
            </div>
            <button 
              onClick={handleApplyFilters}
              className="btn btn-primary"
            >
              Aplicar Filtros
            </button>
            <Link 
              to="/torrefacoes/create"
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Nova Torrefação
            </Link>
          </div>
        </div>
      </div>

      {/* Tabela de Torrefações */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Torrefação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Localização
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {places.map((place) => (
                <tr key={place.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {place.logoUrl ? (
                        <img 
                          src={place.logoUrl} 
                          alt="Logo da torrefação" 
                          className="h-8 w-8 rounded-lg object-cover mr-3"
                        />
                      ) : (
                        <div className="h-8 w-8 bg-gray-200 rounded-lg flex items-center justify-center mr-3">
                          <span className="text-gray-500 text-xs">T</span>
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{place.name}</div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">{place.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {place.cityName}, {place.stateName}
                    </div>
                    <div className="text-sm text-gray-500">{place.address}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      place.status === 'Active' 
                        ? 'bg-green-100 text-green-800' 
                        : place.status === 'Inactive'
                        ? 'bg-red-100 text-red-800'
                        : place.status === 'Pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {place.status === 'Active' ? 'Ativa' : 
                       place.status === 'Inactive' ? 'Inativa' :
                       place.status === 'Pending' ? 'Pendente' : place.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <Link
                        to={`/torrefacoes/${place.id}`}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Visualizar"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                      <Link
                        to={`/torrefacoes/${place.id}/edit`}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                      <button
                        onClick={() => handleDelete(place)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {pagination && pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando{' '}
                  <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span>
                  {' '}até{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, pagination.totalCount)}
                  </span>
                  {' '}de{' '}
                  <span className="font-medium">{pagination.totalCount}</span>
                  {' '}resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-emerald-50 border-emerald-500 text-emerald-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próximo
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Confirmar Exclusão</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Tem certeza que deseja excluir a torrefação "{placeToDelete?.name}"? 
                  Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={() => setDeleteModalOpen(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TorrefacoesPage;